import EnglishReport from '~/components/english/EnglishReport';
import { EmptyState, Tone, WaitingInline } from '~/components/ui';
import { useLocale, useT } from '~/i18n';
import { ENGLISH_STATUS_TONES, isActive } from '~/lib/englishAssessment';
import EnglishCallCard from './components/EnglishCallCard';
import { fetchMyEnglishRecordingUrl } from './api/englishClient';
import { useEnglishAssessments } from './lib/useEnglishAssessments';

/**
 * Perfil → Inglés (`/perfil/ingles`): adonde llevan los correos de la
 * evaluación. Arriba las que esperan su llamada; abajo los resultados, con el
 * mismo informe que ve la empresa.
 */
export default function JobApplyEnglishPage() {
  const t = useT('english');
  useLocale();
  const { data, error, loading, setData } = useEnglishAssessments();

  if (loading) return <WaitingInline />;
  if (error || !data) return <div className="alert alert-error text-sm">{t.candidate.loadError}</div>;

  const active = data.assessments.filter((a) => isActive(a.status));
  const past = data.assessments.filter((a) => !isActive(a.status));

  if (!data.assessments.length) {
    return <EmptyState icon="mic" title={t.candidate.emptyTitle} note={t.candidate.emptyNote} />;
  }

  const onPhoneSaved = (phone: string) =>
    setData({
      ...data,
      whatsapp_phone_e164: phone,
      whatsapp_phone_confirmed: true,
      assessments: data.assessments.map((a) => (isActive(a.status) ? { ...a, phone_e164: phone } : a)),
    });

  return (
    <div className="space-y-4">
      {active.map((assessment) => (
        <EnglishCallCard
          key={assessment._id}
          assessment={assessment}
          phone={data.whatsapp_phone_e164}
          confirmed={data.whatsapp_phone_confirmed}
          line={data.line}
          onPhoneSaved={onPhoneSaved}
        />
      ))}

      {past.length > 0 && active.length > 0 && <h2 className="brand-label">{t.candidate.pastTitle}</h2>}

      {past.map((assessment) => {
        const company = assessment.agency.name || assessment.posting.company || '';
        return (
          <div key={assessment._id} className="card rise">
            <div className="card-body space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <h2 className="card-title text-lg">{t.candidate.resultTitle}</h2>
                  <p className="text-sm hush">{t.candidate.resultFor(company, assessment.posting.title ?? '')}</p>
                </div>
                <Tone tone={ENGLISH_STATUS_TONES[assessment.status]}>{t.status[assessment.status]}</Tone>
              </div>
              {assessment.status === 'completed' && assessment.result ? (
                <>
                  <EnglishReport
                    result={assessment.result}
                    transcript={assessment.transcript}
                    recording={assessment.recording}
                    loadRecording={() => fetchMyEnglishRecordingUrl(assessment._id)}
                  />
                  {company && <p className="text-xs hush">{t.candidate.sharedWith(company)}</p>}
                </>
              ) : (
                <p className="text-sm hush">
                  {assessment.status === 'expired'
                    ? t.candidate.expiredNote
                    : assessment.status === 'canceled'
                      ? t.candidate.canceledNote
                      : t.candidate.failedNote}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
